'use client';

import { createStore } from 'zustand';

import { EnvironmentNameEnum, getEnvironmentNameFromUrl } from '@/config/environment';
import { AuthenticatedUser } from '@/types/auth-base.types';
import { tryToLoadAuthenticatedUser } from '@/services/auth/user.service';
import { getAuthTokens, removeAuthTokens } from '@/storage/auth-tokens.storage';
import { setAuthSessionHint, removeAuthSessionHint } from '@/storage/auth-session-hint.storage';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export type AuthStoreState = {
  authenticatedUser: AuthenticatedUser | undefined;
  authStatus: AuthStatus;
};

export type ComputedAuthStoreState = {
  computed: {
    isLoggedIn: boolean;
    isAuthReady: boolean;
  };
};

const initialAuthStatus: AuthStatus
  = typeof window !== 'undefined' && getAuthTokens()?.access_token ? 'loading' : 'unauthenticated';

export const authStore = createStore<AuthStoreState & ComputedAuthStoreState>((set, get) => {
  return {
    authenticatedUser: undefined,
    authStatus: initialAuthStatus,
    computed: {
      get isLoggedIn() {
        return !!get().authenticatedUser;
      },
      get isAuthReady() {
        return get().authStatus !== 'loading';
      },
    },
  };
});

export const setAuthenticatedUser = (user: AuthenticatedUser | undefined) => {
  authStore.setState({
    authenticatedUser: user,
    authStatus: user ? 'authenticated' : 'unauthenticated',
  });
  setAuthSessionHint(!!user);
};

if (typeof window !== 'undefined' && getEnvironmentNameFromUrl() === EnvironmentNameEnum.LOCAL) {
  (window as unknown as { __medprevAuthStore?: typeof authStore }).__medprevAuthStore = authStore;
}

export async function logoutUser() {
  authStore.setState({ authenticatedUser: undefined, authStatus: 'unauthenticated' });
  removeAuthTokens();
  removeAuthSessionHint();
}

export async function resolveAuthenticatedUser(accessToken?: string) {
  const token = accessToken || getAuthTokens()?.access_token;
  if (!token) {
    authStore.setState({ authStatus: 'unauthenticated' });
    setAuthSessionHint(false);
    return;
  }
  const user = await tryToLoadAuthenticatedUser(token);
  if (!user) {
    authStore.setState({ authStatus: 'unauthenticated' });
    setAuthSessionHint(false);
    return;
  }
  setAuthenticatedUser(user);
}
